import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import bg from "../../assets/bg-hero.jpg";
import { ChatInterface, Message } from '../../components/Chat/ChatInterface';
import { booksChatService } from './chatService';
import { Loader2 } from 'lucide-react';

export const BooksSessionChat = () => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const initializeChat = async () => {
      if (!userId) {
        console.error('No user ID available');
        setIsLoading(false);
        return;
      }

      try {
        const response = await booksChatService.startChat(userId, 'chat_to_books_summary');
        setSessionId(response.sessionId);
        setMessages([{
          role: 'coach',
          content: response.firstMessage
        }]);
      } catch (error) {
        console.error('Failed to initialize chat:', error);
        // Fallback message if API fails
        setMessages([{
          role: 'coach',
          content: "Hey there! What goal are we working on today?"
        }]);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();
  }, [userId]);

  const handleSendMessage = async (message: string) => {
    if (!sessionId || isSending) return;

    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: message
    }]);

    setIsSending(true);

    try {
      const response = await booksChatService.continueChat({
        sessionId,
        userMessage: message,
        categoryPrompt: 'chat_to_books_summary'
      });

      // Add coach response to chat
      setMessages(prev => [...prev, {
        role: 'coach',
        content: response.message
      }]);

      // Show continue button if chat is complete (continue: false)
      if (!response.continue) {
        setShowContinueButton(true);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'coach',
        content: "I'm sorry, there was an error processing your message. Please try again."
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleContinue = () => {
    if (userId && sessionId) {
      navigate(`/loading-audios?userId=${userId}&sessionId=${sessionId}&sessionType=books`);
    } else {
      navigate('/books');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex flex-col items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#79c9cb] animate-spin mx-auto" />
          <p className="text-white text-lg">Starting your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-[#1a1a1a] bg-cover bg-center bg-no-repeat flex flex-col"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="flex-1 flex flex-col mx-4 sm:mx-8 md:mx-16 lg:mx-32 my-4 sm:my-6 md:my-8 lg:my-10">
        <ChatInterface
          messages={messages}
          onSendMessage={handleSendMessage}
          placeholder="Type your answers here..."
          isLoading={isSending}
          showContinueButton={showContinueButton}
          onContinue={handleContinue}
        />
      </div>
    </div>
  );
};